import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchInvoiceDashboard, fetchInvoiceList, fetchBillingEntities, fetchTenant, fetchFarmfolioMetricsAccount, fetchCrmEntities, fetchHorses, fetchInvoiceStandardItems } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Plus, Search, DollarSign, FileText, Download, Mail, Eye, Trash2, Loader2, RefreshCw, ClipboardList, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { TableToolbar } from '../../components/common/TableToolbar';
import OnboardingBeacon from '../../components/onboarding/OnboardingBeacon';
import { useOnboarding } from '../../context/OnboardingContext';
import GenerateCareInvoiceModal from '../../components/farmfolio/GenerateCareInvoiceModal';

import { calcInvoiceAmount } from '../../utils/invoiceCalc';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { Checkbox } from '../../components/ui/checkbox';

const statusBadge = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  delivered_manually: 'bg-green-100 text-green-700',
  delivered_via_greendoor: 'bg-green-100 text-green-700',
};
const paymentBadge = {
  paid: 'bg-green-100 text-green-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
  overdue: 'bg-red-100 text-red-700',
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
};

const InvoicesListPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { completeItem } = useOnboarding();
  const { user } = useAuth();
  const qc = useQueryClient();

  // ── Server state — staleTime:0 so draft amounts are always live ──────────
  const INVOICE_QUERY_OPTS = { staleTime: 0, refetchOnMount: 'always' };

  const { data: dashboard, isLoading: loadingDash } = useQuery({
    queryKey: QK.invoiceDashboard(),
    queryFn: fetchInvoiceDashboard(),
    ...INVOICE_QUERY_OPTS,
  });

  const { data: invoices = [], isLoading: loadingInv } = useQuery({
    queryKey: QK.accountInvoices(),
    queryFn: fetchInvoiceList(),
    ...INVOICE_QUERY_OPTS,
  });

  const loading = loadingDash || loadingInv;

  const { data: billingEntitiesData = [] } = useQuery({
    queryKey: QK.billingEntities(),
    queryFn: fetchBillingEntities(),
  });
  const billingEntities = billingEntitiesData;

  const { data: tenant } = useQuery({
    queryKey: QK.tenant(user?.tenant_id),
    queryFn: fetchTenant(user?.tenant_id),
    enabled: !!user?.tenant_id,
  });
  const tenantName = tenant?.name ?? '';
  const isFarm     = tenant?.is_farm === true;

  const { data: farmMetrics } = useQuery({
    queryKey: QK.farmfolioMetricsAccount('invoices'),
    queryFn: () => import('../../services/api').then(({ default: api }) => api.get('/farmfolio/metrics/invoices').then(r => r.data)),
    enabled: isFarm,
  });

  const invalidateInvoices = () => {
    qc.invalidateQueries({ queryKey: QK.invoiceDashboard() });
    qc.invalidateQueries({ queryKey: QK.accountInvoices() });
  };

  // ── UI state ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [careInvoiceOpen, setCareInvoiceOpen] = useState(false);
  const [metricsView, setMetricsView] = useState('ytd');

  const filtered = invoices
    .filter(inv => {
      if (statusFilter !== 'all' && inv.invoice_status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (inv.crm_entity_name || '').toLowerCase().includes(q) ||
               (inv.horse_name || '').toLowerCase().includes(q) ||
               (inv.invoice_number_formatted || '').toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by the displayed number (trailing digits of invoice_number_formatted)
      // so a manually-renumbered invoice sorts by what the user sees, not the raw sequence integer.
      const num = (inv) => {
        const m = (inv.invoice_number_formatted || '').match(/(\d+)$/);
        return m ? parseInt(m[1], 10) : (inv.invoice_number || 0);
      };
      return num(b) - num(a);
    });

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-semibold">Invoices</h1>
            <p className="text-muted-foreground mt-1">Manage billing and invoicing</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <OnboardingBeacon itemKey="create_invoice">
              <Button onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90 text-white w-full sm:w-auto" data-testid="new-invoice-btn">
                <Plus className="w-4 h-4 mr-2" /> New Invoice
              </Button>
            </OnboardingBeacon>
            {isFarm && (
              <Button variant="outline" onClick={() => setCareInvoiceOpen(true)} className="w-full sm:w-auto" data-testid="care-invoice-btn">
                <FileText className="w-4 h-4 mr-2" /> + New FarmFolio Invoice
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/invoices/statements')} className="w-full sm:w-auto" data-testid="statements-nav-btn">
              <ClipboardList className="w-4 h-4 mr-2" /> Statements
            </Button>
            <Button variant="outline" onClick={() => navigate('/invoices/recurring')} className="w-full sm:w-auto" data-testid="recurring-nav-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Recurring
            </Button>
            <Button variant="outline" onClick={() => navigate('/invoices/standard-items')} className="w-full sm:w-auto" data-testid="saved-items-nav-btn">
              <FileText className="w-4 h-4 mr-2" /> Saved Invoice Items
            </Button>
          </div>
        </div>

        {/* Top Line Summary */}
        {dashboard && (
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Line Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main boxes — always shown */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Box 1: Total Invoice Amount */}
                <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                  <p className="text-xs text-muted-foreground uppercase">Total Billed Amount</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{formatCurrency(dashboard.total_invoice_ytd ?? 0)}</span></div>
                    <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{formatCurrency(dashboard.total_invoice_all_time ?? 0)}</span></div>
                  </div>
                </div>
                {/* Box 2: YTD by Status */}
                <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                  <p className="text-xs text-muted-foreground uppercase">YTD by Status</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-xs font-medium">Draft:</span><span className="text-xs">{formatCurrency(dashboard.draft_total_ytd)}</span></div>
                    <div className="flex justify-between"><span className="text-xs font-medium">Approved:</span><span className="text-xs">{formatCurrency(dashboard.approved_total_ytd)}</span></div>
                    <div className="flex justify-between"><span className="text-xs font-medium">Sent Out:</span><span className="text-xs">{formatCurrency(dashboard.delivered_total_ytd)}</span></div>
                  </div>
                </div>
                {/* Box 3: YTD Payment Status */}
                <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                  <p className="text-xs text-muted-foreground uppercase">YTD Payment Status</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-xs font-medium">Paid:</span><span className="text-xs text-primary">{formatCurrency(dashboard.paid_total_ytd)}</span></div>
                    <div className="flex justify-between"><span className="text-xs font-medium">Unpaid:</span><span className="text-xs text-red-600">{formatCurrency(dashboard.unpaid_total_ytd)}</span></div>
                  </div>
                </div>
              </div>

              {/* Sales Summary — shown if tenant has sales activity */}
              {(dashboard.gross_sales_all > 0 || dashboard.all_commissions_total > 0) && (
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase hover:text-foreground transition-colors w-full text-left">
                    <ChevronDown className="w-3.5 h-3.5" />
                    Sales Summary
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                      <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                        <p className="text-xs text-muted-foreground uppercase">All Commissions</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{formatCurrency(dashboard.all_commissions_total_ytd)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{formatCurrency(dashboard.all_commissions_total)}</span></div>
                        </div>
                      </div>
                      <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                        <p className="text-xs text-muted-foreground uppercase">Gross Sales</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{formatCurrency(dashboard.gross_sales_ytd)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{formatCurrency(dashboard.gross_sales_all)}</span></div>
                        </div>
                      </div>
                      <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                        <p className="text-xs text-muted-foreground uppercase">Owner Net Proceeds</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{formatCurrency(dashboard.owner_net_proceeds_ytd)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{formatCurrency(dashboard.owner_net_proceeds_all)}</span></div>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* FarmFolio Summary — shown for farm tenants */}
              {isFarm && farmMetrics && (
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase hover:text-foreground transition-colors w-full text-left">
                    <ChevronDown className="w-3.5 h-3.5" />
                    FarmFolio Summary
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                      <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                        <p className="text-xs text-muted-foreground uppercase">Total Revenue</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{formatCurrency(farmMetrics.total_revenue?.ytd ?? 0)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{formatCurrency(farmMetrics.total_revenue?.all_time ?? 0)}</span></div>
                        </div>
                      </div>
                      <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                        <p className="text-xs text-muted-foreground uppercase">Net Revenue</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{formatCurrency(farmMetrics.net_revenue?.ytd ?? 0)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{formatCurrency(farmMetrics.net_revenue?.all_time ?? 0)}</span></div>
                        </div>
                      </div>
                      <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                        <p className="text-xs text-muted-foreground uppercase">Accounts Receivable</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span className="text-xs font-medium">30 days:</span><span className="text-xs">{formatCurrency(farmMetrics.ar_aging?.['30_days'] ?? 0)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">60 days:</span><span className="text-xs">{formatCurrency(farmMetrics.ar_aging?.['60_days'] ?? 0)}</span></div>
                          <div className="flex justify-between"><span className="text-xs font-medium">90+ days:</span><span className={`text-xs ${(farmMetrics.ar_aging?.['90_plus'] ?? 0) > 0 ? 'text-red-600 font-semibold' : ''}`}>{formatCurrency(farmMetrics.ar_aging?.['90_plus'] ?? 0)}</span></div>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </CardContent>
          </Card>

          {/* Per-billing-entity breakdown — only shown when secondary entities exist */}
          {billingEntities.length > 0 && dashboard?.by_entity?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dashboard.by_entity.map(ent => (
                <Card key={ent.billing_entity_id || 'primary'} className="border-dashed">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm text-muted-foreground">
                      {ent.billing_entity_id ? ent.name : (tenantName || ent.name)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">Invoices:</span><span>{ent.total_count}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">Paid YTD:</span><span className="text-primary">{formatCurrency(ent.paid_total_ytd)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">Unpaid YTD:</span><span className="text-red-600">{formatCurrency(ent.unpaid_total_ytd)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">Commissions YTD:</span><span>{formatCurrency(ent.commissions_ytd)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-2">
              {/* Search — full width */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search invoices..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-full" />
              </div>
              {/* Status filter — full width on its own row */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
              {/* Export CSV — right-aligned */}
              <div className="flex justify-end">
                <TableToolbar
                  inline
                  tableId="invoices-list"
                  data={filtered}
                  filename="invoices"
                  defaultHidden={['billing_entity', 'payment_notes']}
                  columns={[
                    { key: 'billing_entity', label: 'Billing Entity', accessor: r => {
                        if (!r.billing_entity_id) return tenantName || 'Primary';
                        return billingEntities.find(e => e.id === r.billing_entity_id)?.name || r.billing_entity_id;
                    }},
                    { key: 'invoice_number', label: 'Invoice #', accessor: r => r.invoice_number_formatted || `#${r.invoice_number}` },
                    { key: 'crm_entity_name', label: 'Account / Owner' },
                    { key: 'horse_name', label: 'Horse' },
                    { key: 'invoice_date', label: 'Date', accessor: r => r.invoice_date ? new Date(r.invoice_date).toLocaleDateString() : '-' },
                    { key: 'owner_portion_total', label: 'Amount', accessor: r => formatCurrency(r.owner_portion_total || r.total_amount) },
                    { key: 'invoice_status', label: 'Status' },
                    { key: 'payment_status', label: 'Payment' },
                    { key: 'payment_notes', label: 'Payment Notes', accessor: r => r.payment_notes || '' },
                  ]}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                <p className="text-muted-foreground">{search || statusFilter !== 'all' ? 'No matching invoices' : 'No invoices yet. Click "New Invoice" to create one.'}</p>
              </div>
            ) : (
              <div>
              <Table data-table-id="invoices-list">
                <TableHeader>
                  <TableRow>
                    <TableHead>Billing Entity</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Account / Owner</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Payment Notes</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(inv => (
                    <TableRow key={inv.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/invoices/${inv.id}`)}>
                      <TableCell>
                        {inv.billing_entity_id
                          ? (billingEntities.find(e => e.id === inv.billing_entity_id)?.name || '–')
                          : (tenantName || '–')}
                      </TableCell>
                      <TableCell className="font-medium">{inv.invoice_number_formatted || `#${inv.invoice_number}`}</TableCell>
                      <TableCell>{inv.crm_entity_name || '-'}</TableCell>
                      <TableCell>{inv.horse_name || '-'}</TableCell>
                      <TableCell className="text-sm">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell className="font-medium">{(() => {
                        const { amount, isPayable } = calcInvoiceAmount(inv);
                        return isPayable ? `(${formatCurrency(Math.abs(amount))})` : formatCurrency(Math.abs(amount));
                      })()}</TableCell>
                      <TableCell><Badge className={statusBadge[inv.invoice_status] || 'bg-gray-100'}>{inv.invoice_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                      <TableCell><Badge className={paymentBadge[inv.payment_status] || 'bg-gray-100'}>{inv.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{inv.payment_notes || '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={async (e) => {
                          e.stopPropagation();
                          if (!await confirm({ title: 'Delete this invoice?', destructive: true })) return;
                          try {
                            await api.delete(`/invoices/${inv.id}`);
                            toast.success('Invoice deleted');
                            invalidateInvoices();
                          } catch { toast.error('Failed to delete'); }
                        }} title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateInvoiceDialog open={createOpen} onOpenChange={setCreateOpen} tenantName={tenantName} onCreated={(id) => { setCreateOpen(false); if (id) navigate(`/invoices/${id}`); else invalidateInvoices(); }} />
      <GenerateCareInvoiceModal open={careInvoiceOpen} onClose={() => setCareInvoiceOpen(false)} />
    </SidebarLayout>
  );
};

const DashCard = ({ label, value, accent, warn, tall }) => (
  <Card className={tall ? 'row-span-1' : ''}>
    <CardContent className={`${tall ? 'p-4 h-full flex flex-col justify-between' : 'p-3'}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-serif ${tall ? 'text-xl' : 'text-lg'} font-semibold ${accent ? 'text-primary' : warn ? 'text-red-600' : ''}`}>{value}</p>
    </CardContent>
  </Card>
);

/* --- Create Invoice Dialog --- */
const CreateInvoiceDialog = ({ open, onOpenChange, onCreated, tenantName }) => {
  const { completeItem } = useOnboarding();
  const { isSyndicator } = useAuth();

  // ── useQuery subscriptions with enabled:open — renders with cached data instantly
  // when warm; fetches and re-renders automatically on cold cache (first visit). ──
  const { data: entitiesData = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
    enabled: open,
  });
  const { data: horsesData = [] } = useQuery({
    queryKey: QK.horses({ include_archived: true }),
    queryFn: fetchHorses({ include_archived: true }),
    enabled: open,
  });
  const { data: stdItemsData = [] } = useQuery({
    queryKey: QK.invoiceStandardItems(),
    queryFn: fetchInvoiceStandardItems(),
    enabled: open,
  });
  const { data: billingEntitiesData = [] } = useQuery({
    queryKey: QK.billingEntities(),
    queryFn: fetchBillingEntities(),
    enabled: open,
  });

  // Alias to the variable names used throughout the render
  const entities        = entitiesData;
  const horses          = horsesData;
  const standardItems   = stdItemsData;
  const billingEntities = billingEntitiesData;

  const [form, setForm] = useState({ crm_entity_id: '', horse_id: '', entries: [], add_sale_proceeds: false, sale_net_proceeds: 0, sale_opportunity_id: '', other_notes_terms: '', add_discount: false, discount_amount: 0, discount_unit: 'currency', billing_entity_id: '' });
  const [saving, setSaving] = useState(false);
  const [saleOpps, setSaleOpps] = useState([]);
  const [showBillingOverride, setShowBillingOverride] = useState(false);
  const [syndicates, setSyndicates] = useState([]);
  const [selectedSyndicateId, setSelectedSyndicateId] = useState('');
  const [syndicateMembers, setSyndicateMembers] = useState([]);
  const [syndicateHorseIds, setSyndicateHorseIds] = useState([]);

  useEffect(() => {
    if (open) {
      setForm({ crm_entity_id: '', horse_id: '', invoice_date: new Date().toISOString().split('T')[0], po_number: '', period_start_date: '', period_end_date: '', entries: [{ items: '', description: '', units: 1, price: 0 }], add_sale_proceeds: false, sale_net_proceeds: 0, sale_opportunity_id: '', other_notes_terms: '', add_discount: false, discount_amount: 0, discount_unit: 'currency', billing_entity_id: '', owner_percent: 100 });
      setSaleOpps([]);
      setSelectedSyndicateId('');
      setSyndicateMembers([]);
      setSyndicateHorseIds([]);
      // Load syndicates for syndicator users (not in standard cache)
      if (isSyndicator) {
        api.get('/crm-entities/managed-syndicates').then(r => setSyndicates(r.data || [])).catch(() => {});
      }
    }
  // eslint-disable-next-line
  }, [open]);


  const handleSyndicateSelect = async (syndicateId) => {
    setSelectedSyndicateId(syndicateId);
    setForm(f => ({ ...f, crm_entity_id: '', horse_id: '', sale_opportunity_id: '', sale_net_proceeds: 0, owner_percent: 100 }));
    setSaleOpps([]);
    if (!syndicateId) { setSyndicateMembers([]); setSyndicateHorseIds([]); return; }
    try {
      const [membersRes, horsesRes] = await Promise.all([
        api.get(`/syndicate-ownership/${syndicateId}`),
        api.get(`/ownership-records?entity_id=${syndicateId}&is_current=true`),
      ]);
      setSyndicateMembers(membersRes.data || []);
      setSyndicateHorseIds((horsesRes.data || []).map(r => r.horse_id));
    } catch {
      setSyndicateMembers([]);
      setSyndicateHorseIds([]);
    }
  };


  const fetchSaleOpps = async (horseId) => {
    if (!horseId) { setSaleOpps([]); return; }
    try {
      const res = await api.get('/sale-opportunities');
      const opps = (res.data || []).filter(o => o.horse_id === horseId && !o.archived);
      // Enrich with sale name
      const enriched = await Promise.all(opps.map(async (opp) => {
        try {
          const saleRes = await api.get(`/sales/${opp.sale_id}`);
          const yr = saleRes.data?.sale_start_date?.substring(0, 4) || '';
          return { ...opp, sale_label: `${yr} ${saleRes.data?.sale_company || ''} - ${saleRes.data?.sale_name || ''}` };
        } catch { return { ...opp, sale_label: '' }; }
      }));
      setSaleOpps(enriched);
    } catch { setSaleOpps([]); }
  };

  const handleSelectSaleOpp = async (oppId) => {
    try {
      const res = await api.get(`/invoices/generate-from-sale/${oppId}`);
      setForm(f => ({ ...f, sale_opportunity_id: oppId, sale_net_proceeds: res.data.net_proceeds || 0 }));
    } catch { /* ignore */ }
  };

  const addEntry = (item) => {
    if (item) {
      setForm(f => ({ ...f, entries: [...f.entries, { items: item.items, description: item.description, units: 1, price: item.price }] }));
    } else {
      setForm(f => ({ ...f, entries: [...f.entries, { items: '', description: '', units: 1, price: 0 }] }));
    }
  };

  const updateEntry = (idx, field, value) => {
    setForm(f => {
      const entries = [...f.entries];
      entries[idx] = { ...entries[idx], [field]: value };
      return { ...f, entries };
    });
  };

  const removeEntry = (idx) => {
    setForm(f => ({ ...f, entries: f.entries.filter((_, i) => i !== idx) }));
  };

  const [commissionSplitChoice, setCommissionSplitChoice] = useState(null); // {commissionRate, splitPercent, splitEntityName}

  const handleCreate = async () => {
    if (!form.crm_entity_id) { toast.error('Please select a client'); return; }
    if (form.entries.length === 0 && !form.add_sale_proceeds) { toast.error('Please add at least one line item or include sale proceeds'); return; }

    // Check for commission split if sale proceeds included
    if (form.add_sale_proceeds && form.sale_net_proceeds > 0 && form.crm_entity_id) {
      try {
        const entityRes = await api.get(`/crm-entities/${form.crm_entity_id}`);
        if (!entityRes.data?.id) throw new Error('Not a CRM entity');
        const entity = entityRes.data;
        const commissionRate = parseFloat(entity?.commission_rate_percent || 0);
        const hasSplit = entity?.commission_split || false;
        const splitPercent = parseFloat(entity?.commission_split_percent_linked || 0);
        const splitEntityId = entity?.commission_split_with_entity_id || '';
        let splitEntityName = '';
        if (hasSplit && splitEntityId) {
          try {
            const splitRes = await api.get(`/crm-entities/${splitEntityId}`);
            splitEntityName = splitRes.data?.system_name || '';
          } catch { /* ignore */ }
        }
        if (hasSplit && splitPercent > 0 && commissionRate > 0 && !commissionSplitChoice) {
          const selectedOpp = saleOpps.find(o => o.id === form.sale_opportunity_id);
          setCommissionSplitChoice({
            commissionRate,
            splitPercent,
            splitEntityName: entity?.commission_split_entity_name || entity?.commission_split_percent_entity || '',
            splitEntityId: entity?.commission_split_with_entity_id || '',
            saleName: selectedOpp?.sale_label || '',
          });
          return;
        }
      } catch { /* ignore */ }
    }

    await doCreate(null);
  };

  const doCreate = async (choice) => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Attach syndicate context when creating via syndicator flow
      if (isSyndicator && selectedSyndicateId) {
        payload.syndicate_id = selectedSyndicateId;
      }
      if (choice === 'my_portion') {
        payload.commission_split_my_portion = true;
      } else if (choice === 'full') {
        payload.commission_split_full_with_separate = true;
      }
      const res = await api.post('/invoices', payload);
      const invoiceId = res.data?.id;

      // If full commission chosen, create separate split invoice BEFORE redirecting
      if (choice === 'full' && invoiceId && commissionSplitChoice) {
        const fullCommission = (form.sale_net_proceeds || 0) * (commissionSplitChoice.commissionRate / 100);
        const splitAmount = fullCommission * (commissionSplitChoice.splitPercent / 100);
        try {
          await api.post('/invoices', {
            crm_entity_id: commissionSplitChoice.splitEntityId,
            horse_id: form.horse_id,
            sale_opportunity_id: form.sale_opportunity_id,
            entries: [{ items: 'Commission Split Payment', description: `${commissionSplitChoice.saleName ? commissionSplitChoice.saleName + ' - ' : ''}${commissionSplitChoice.splitPercent}% of ${commissionSplitChoice.commissionRate}% commission from ${commissionSplitChoice.splitEntityName || 'split entity'}`, units: 1, price: splitAmount }],
            add_sale_proceeds: false,
            is_commission_split_invoice: true,
            other_notes_terms: `Commission split payment: ${commissionSplitChoice.saleName ? commissionSplitChoice.saleName + ' - ' : ''}${commissionSplitChoice.splitPercent}% of ${commissionSplitChoice.commissionRate}% commission`,
          });
          toast.success('Commission split invoice also created');
        } catch (err) {
          console.error('Failed to create split invoice:', err);
          toast.error('Failed to create commission split invoice');
        }
      }

      toast.success('Invoice created as draft');
      completeItem('create_invoice');
      setCommissionSplitChoice(null);
      setSaving(false);
      onCreated(invoiceId);
    } catch (err) {
      const errMsg = err?.response?.data?.detail || err?.message || '';
      console.error('Invoice create error:', errMsg, err);
      toast.error('Failed to create invoice. The invoice may have been created — please check the list.');
      setSaving(false);
    }
  };

  const filteredEntities = entities.filter(e => e.crm_class === 'account' && !e.is_tenant_managed);
  // When syndicator: only show accounts that are syndicate members; always exclude managed syndicates
  const syndicateMemberIds = syndicateMembers.map(m => m.owner_crm_entity_id);

  // Sort accounts: by last name for individuals, by system_name for organisations
  const sortAccounts = (list) => [...list].sort((a, b) => {
    const keyA = a.person_last_name
      ? `${a.person_last_name} ${a.person_first_name || ''}`.trim()
      : (a.system_name || '');
    const keyB = b.person_last_name
      ? `${b.person_last_name} ${b.person_first_name || ''}`.trim()
      : (b.system_name || '');
    return keyA.localeCompare(keyB);
  });

  const accountOptions = sortAccounts(
    isSyndicator && selectedSyndicateId
      ? filteredEntities.filter(e => syndicateMemberIds.includes(e.id))
      : filteredEntities
  );
  // When syndicator: only show horses the selected syndicate owns — sorted A→Z
  const horseOptions = [...(
    isSyndicator && selectedSyndicateId
      ? horses.filter(h => syndicateHorseIds.includes(h.id))
      : horses
  )].sort((a, b) =>
    (a.system_name || a.name || '').localeCompare(b.system_name || b.name || '')
  );
  // Current member's ownership % for display
  const selectedMemberPct = isSyndicator && selectedSyndicateId && form.crm_entity_id
    ? (syndicateMembers.find(m => m.owner_crm_entity_id === form.crm_entity_id)?.ownership_percent ?? null)
    : null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create New Invoice</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
        {/* Syndicator: Syndicate selector first */}
          {isSyndicator && (
            <div className="space-y-2">
              <Label>Syndicate</Label>
              <Select value={selectedSyndicateId || '_none'} onValueChange={v => handleSyndicateSelect(v === '_none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select syndicate (optional)..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— No syndicate filter —</SelectItem>
                  {syndicates.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.system_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {/* Account & Horse */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account *</Label>
              <Select value={form.crm_entity_id || ''} onValueChange={(v) => {
                // In syndicator mode, auto-set owner_percent from the member's syndicate ownership %
                const memberPct = isSyndicator && selectedSyndicateId
                  ? (syndicateMembers.find(m => m.owner_crm_entity_id === v)?.ownership_percent ?? 100)
                  : 100;
                setForm(f => ({ ...f, crm_entity_id: v, owner_percent: memberPct }));
              }}>
                <SelectTrigger><SelectValue placeholder="Select account..." /></SelectTrigger>
                <SelectContent>
                  {accountOptions.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.system_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Horse</Label>
              <Select value={form.horse_id || ''} onValueChange={(v) => {
                const horse = horses.find(h => h.id === v);
                setForm(f => ({
                  ...f,
                  horse_id: v,
                  sale_opportunity_id: '',
                  sale_net_proceeds: 0,
                  billing_entity_id: horse?.default_billing_entity_id || f.billing_entity_id,
                }));
                setShowBillingOverride(false);
                fetchSaleOpps(v);
              }}>
                <SelectTrigger><SelectValue placeholder="Select horse (optional)" /></SelectTrigger>
                <SelectContent>
                  {horseOptions.map(h => <SelectItem key={h.id} value={h.id}>{h.system_name || h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Invoice From — only shown if secondary billing entity exists */}
          {billingEntities.length > 0 && (() => {
            const selectedHorse = horses.find(h => h.id === form.horse_id);
            const hasDefault = !!selectedHorse?.default_billing_entity_id;
            const currentEntityId = form.billing_entity_id;
            const currentEntityName = currentEntityId
              ? billingEntities.find(be => be.id === currentEntityId)?.name
              : (tenantName || 'Primary Organization');

            // If horse has a default and user hasn't requested to override, show locked display
            if (hasDefault && !showBillingOverride) {
              return (
                <div className="space-y-1">
                  <Label>Invoice From</Label>
                  <div className="flex items-center justify-between h-9 px-3 border rounded-md bg-muted/30">
                    <span className="text-sm">{currentEntityName}</span>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline ml-2 shrink-0"
                      onClick={() => setShowBillingOverride(true)}
                    >
                      Change
                    </button>
                  </div>
                </div>
              );
            }

            // No horse default, or user clicked "Change" — show dropdown
            return (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label>Invoice From</Label>
                  {hasDefault && showBillingOverride && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:underline"
                      onClick={async () => {
                        setForm(f => ({ ...f, billing_entity_id: selectedHorse.default_billing_entity_id }));
                        setShowBillingOverride(false);
                      }}
                    >
                      Reset to default
                    </button>
                  )}
                </div>
                <Select value={form.billing_entity_id || 'primary'} onValueChange={v => setForm(f => ({ ...f, billing_entity_id: v === 'primary' ? '' : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">{tenantName || 'Primary Organization'}</SelectItem>
                    {billingEntities.map(be => <SelectItem key={be.id} value={be.id}>{be.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            );
          })()}

          {/* Invoice Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <Input type="date" className={form.invoice_date ? 'date-filled' : 'text-muted-foreground'} value={form.invoice_date} onChange={(e) => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>PO Number</Label>
              <Input value={form.po_number} onChange={(e) => setForm(f => ({ ...f, po_number: e.target.value }))} placeholder="Optional" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Billing Period Start</Label>
              <Input type="date" className={form.period_start_date ? 'date-filled' : 'text-muted-foreground'} value={form.period_start_date} onChange={(e) => setForm(f => ({ ...f, period_start_date: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Billing Period End</Label>
              <Input type="date" className={form.period_end_date ? 'date-filled' : 'text-muted-foreground'} value={form.period_end_date} onChange={(e) => setForm(f => ({ ...f, period_end_date: e.target.value }))} />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line Items</Label>
              <div className="flex gap-2">
                {standardItems.length > 0 && (
                  <Select onValueChange={(v) => { const item = standardItems.find(s => s.id === v); if (item) addEntry(item); }}>
                    <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="Add from template" /></SelectTrigger>
                    <SelectContent>
                      {standardItems.map(s => <SelectItem key={s.id} value={s.id}>{s.items} (${s.price})</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addEntry()}>
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {form.entries.length > 0 && (
                <div className="grid grid-cols-12 gap-2 px-0.5">
                  <div className="col-span-3 text-xs text-muted-foreground font-medium">Item</div>
                  <div className="col-span-4 text-xs text-muted-foreground font-medium">Description</div>
                  <div className="col-span-2 text-xs text-muted-foreground font-medium">Qty</div>
                  <div className="col-span-2 text-xs text-muted-foreground font-medium">Unit Price</div>
                  <div className="col-span-1"></div>
                </div>
              )}
              {form.entries.map((entry, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3"><Input className="h-8 text-xs" placeholder="Item" value={entry.items} onChange={(e) => updateEntry(idx, 'items', e.target.value)} /></div>
                  <div className="col-span-4"><Input className="h-8 text-xs" placeholder="Description" value={entry.description} onChange={(e) => updateEntry(idx, 'description', e.target.value)} /></div>
                  <div className="col-span-2"><Input className="h-8 text-xs" type="number" placeholder="Units" value={entry.units} onChange={(e) => updateEntry(idx, 'units', parseFloat(e.target.value) || 1)} /></div>
                  <div className="col-span-2"><Input className="h-8 text-xs" type="number" placeholder="Price" value={entry.price} onChange={(e) => updateEntry(idx, 'price', parseFloat(e.target.value) || 0)} /></div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500" onClick={() => removeEntry(idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-right">
              Subtotal: {formatCurrency(form.entries.reduce((sum, e) => sum + (parseFloat(e.price) || 0) * (parseFloat(e.units) || 1), 0))}
            </p>
          </div>

          {/* Sale Proceeds */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!form.add_sale_proceeds} onCheckedChange={(v) => setForm(f => ({ ...f, add_sale_proceeds: v }))} />
              Include Sale Proceeds
            </label>
            {form.add_sale_proceeds && (
              <div className="space-y-2 pl-6">
                {form.horse_id ? (
                  <div className="space-y-2">
                    <Label>Sale Opportunity</Label>
                    <Select value={form.sale_opportunity_id || ''} onValueChange={handleSelectSaleOpp}>
                      <SelectTrigger className="text-sm"><SelectValue placeholder="Select sale opportunity..." /></SelectTrigger>
                      <SelectContent>
                        {saleOpps.length === 0 ? (
                          <SelectItem value="none" disabled>No sale opportunities for this horse</SelectItem>
                        ) : (
                          saleOpps.map(o => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.opportunity_name?.split(' - ')[1] || o.opportunity_name} — {o.sale_label} (Hip #{o.hip_number || 'TBD'})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {form.sale_net_proceeds > 0 && (
                      <p className="text-xs text-muted-foreground">Net Proceeds: {formatCurrency(form.sale_net_proceeds)}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Please select a Horse first to see sale opportunities.</p>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes / Terms</Label>
            <Textarea rows={2} value={form.other_notes_terms} onChange={(e) => setForm(f => ({ ...f, other_notes_terms: e.target.value }))} placeholder="Additional notes or terms..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Creating...' : 'Create Draft Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Commission Split Choice Dialog */}
    {commissionSplitChoice && !commissionSplitChoice.choice && (
      <Dialog open={true} onOpenChange={() => setCommissionSplitChoice(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Commission Split</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm">
              This client has a <strong>{commissionSplitChoice.splitPercent}%</strong> commission split
              {commissionSplitChoice.splitEntityName && ` with ${commissionSplitChoice.splitEntityName}`}.
            </p>
            <p className="text-sm text-muted-foreground">
              How should the {commissionSplitChoice.commissionRate}% commission be reflected?
            </p>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start text-left h-auto py-3" onClick={async () => {
                setCommissionSplitChoice(prev => ({ ...prev, choice: 'my_portion' }));
                doCreate('my_portion');
              }}>
                <div>
                  <p className="font-medium">My Portion Only</p>
                  <p className="text-xs text-muted-foreground">Show {100 - commissionSplitChoice.splitPercent}% of {commissionSplitChoice.commissionRate}% commission</p>
                </div>
              </Button>
              <Button variant="outline" className="w-full justify-start text-left h-auto py-3" onClick={async () => {
                setCommissionSplitChoice(prev => ({ ...prev, choice: 'full' }));
                doCreate('full');
              }}>
                <div>
                  <p className="font-medium">Full Commission</p>
                  <p className="text-xs text-muted-foreground">Show full {commissionSplitChoice.commissionRate}% and create separate invoice for the {commissionSplitChoice.splitPercent}% split</p>
                </div>
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommissionSplitChoice(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};

export default InvoicesListPage;
